import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, ChevronLeft, ChevronRight, Camera, X } from 'lucide-react';
import AdminLayout from '../../layouts/AdminLayout';
import { ordersApi } from '../../services/api';
import { formatCurrency, formatDate, StatusBadge, PaymentBadge } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';

function QrScanModal({ onClose, onScanned }) {
  const scannerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let scanner = null;
    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        scanner = new Html5Qrcode('qr-reader-modal');
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (decodedText) => {
            // Extract tracking code from URL or use raw text
            let code = decodedText.trim().toUpperCase();
            const urlMatch = code.match(/\/TRACK\/([A-Z0-9]+)/i);
            if (urlMatch) code = urlMatch[1].toUpperCase();
            onScanned(code);
          },
          () => {}
        );
      } catch (err) {
        toast.error('Kamera tidak dapat diakses. Pastikan izin kamera diberikan.');
        onClose();
      }
    };
    startScanner();
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
      }
    };
  }, []); // eslint-disable-line

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Camera size={18} className="text-[#0f6c44]" />
            <h3 className="font-bold text-slate-800">Scan QR Nota</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-xl transition-colors">
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="p-4">
          <div id="qr-reader-modal" className="w-full rounded-xl overflow-hidden" ref={containerRef} />
          <p className="text-xs text-slate-400 text-center mt-3">Arahkan kamera ke QR code pada nota pesanan</p>
        </div>
      </div>
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: '', label: { id: 'Semua Status', en: 'All Status' } },
  { value: 'masuk', label: { id: 'Masuk', en: 'Received' } },
  { value: 'proses_cuci', label: { id: 'Proses Cuci', en: 'Washing' } },
  { value: 'siap_diambil', label: { id: 'Siap Diambil', en: 'Ready' } },
  { value: 'selesai', label: { id: 'Selesai', en: 'Done' } },
];

export default function OrdersPage() {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [showScanner, setShowScanner] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['orders', { search, status, page }],
    queryFn: () => ordersApi.list({ search, status, page, limit: 15 }).then(r => r.data),
  });

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(1); };

  const handleScanned = async (trackingCode) => {
    setShowScanner(false);
    if (!trackingCode) return;
    try {
      const res = await ordersApi.list({ search: trackingCode, limit: 1, page: 1 });
      const orders = res.data?.orders || res.data || [];
      if (orders.length > 0) {
        navigate(`/admin/orders/${orders[0].id}`);
      } else {
        toast.error(`Pesanan "${trackingCode}" tidak ditemukan`);
      }
    } catch {
      toast.error('Gagal mencari pesanan');
    }
  };

  return (
    <AdminLayout>
      {showScanner && <QrScanModal onClose={() => setShowScanner(false)} onScanned={handleScanned} />}
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Daftar Pesanan', 'Orders')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{data?.total || 0} {t('total pesanan', 'total orders')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowScanner(true)} data-testid="scan-nota-btn"
              className="flex items-center gap-2 px-3 py-2.5 bg-slate-100 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-200 active:scale-95 transition-all border border-slate-200">
              <Camera size={16} />{t('Scan Nota', 'Scan Note')}
            </button>
            <button onClick={() => navigate('/admin/order-create')} data-testid="create-order-button"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0f6c44] text-white rounded-xl text-sm font-semibold hover:bg-[#0c5636] active:scale-95 transition-all shadow-sm">
              <Plus size={18} />{t('Buat Pesanan', 'New Order')}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-slate-50">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                  placeholder={t('Cari kode/pelanggan...', 'Search code/customer...')}
                  className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44]" />
              </div>
              <button type="submit" className="px-4 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200 transition-colors">
                {t('Cari', 'Search')}
              </button>
            </form>
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-slate-400 flex-shrink-0" />
              <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}
                className="py-2.5 pl-3 pr-8 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-[#0f6c44] cursor-pointer">
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label[lang]}</option>)}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-50">
                  {[t('Kode', 'Code'), t('Pelanggan', 'Customer'), t('Item', 'Items'), t('Total', 'Total'), t('Bayar', 'Payment'), t('Status', 'Status'), t('Tgl', 'Date')].map(h => (
                    <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? [1,2,3,4,5].map(i => (
                  <tr key={i}>{[1,2,3,4,5,6,7].map(j => <td key={j} className="px-5 py-4"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td>)}</tr>
                )) : (data?.items?.length === 0) ? (
                  <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400 text-sm">{t('Tidak ada pesanan', 'No orders found')}</td></tr>
                ) : (data?.items || []).map(order => (
                  <tr key={order.id} onClick={() => navigate(`/admin/orders/${order.id}`)}
                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 cursor-pointer transition-colors">
                    <td className="px-5 py-3.5"><span className="font-mono text-xs font-bold text-[#0f6c44] bg-emerald-50 px-2 py-0.5 rounded">{order.tracking_code}</span></td>
                    <td className="px-5 py-3.5"><div className="text-sm font-medium text-slate-800">{order.customer_name || '-'}</div><div className="text-xs text-slate-400">{order.customer_phone}</div></td>
                    <td className="px-5 py-3.5 text-xs text-slate-500">{order.items?.length || 0} item</td>
                    <td className="px-5 py-3.5 text-sm font-semibold text-slate-800 whitespace-nowrap">{formatCurrency(order.total_amount)}</td>
                    <td className="px-5 py-3.5"><PaymentBadge status={order.payment_status} lang={lang} /></td>
                    <td className="px-5 py-3.5"><StatusBadge status={order.status} lang={lang} /></td>
                    <td className="px-5 py-3.5 text-xs text-slate-400 whitespace-nowrap">{formatDate(order.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(data?.total || 0) > 15 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-50">
              <p className="text-sm text-slate-500">{t('Halaman', 'Page')} {page}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-40 transition-colors">
                  <ChevronLeft size={16} className="text-slate-600" />
                </button>
                <button onClick={() => setPage(p => p + 1)} disabled={(page * 15) >= (data?.total || 0)}
                  className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-40 transition-colors">
                  <ChevronRight size={16} className="text-slate-600" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
